import React, { useMemo, memo } from 'react';
import { format, getHours, getMinutes } from 'date-fns';

/**
 * HourCell - Visual-only grid cell component
 *
 * OPTIMIZED: This component is now purely visual. All interactions (drop, click, touch)
 * are handled by GridDropOverlay at the day level.
 *
 * This reduces:
 * - useDrop hooks from 1,440 to 30 per month view
 * - Event handlers from 8,640 to 180 per month view
 * - Component complexity by ~60%
 */

interface HourCellProps {
  dayDate: Date;
  cellTime: Date;
  businessId?: string | null;
  isHourMarker?: boolean;
  isToday: boolean;
  isWeekendDay: boolean;
  isClosedAllDay: boolean;
  // Opening hours visual indicator props
  isWithinBusinessHours?: boolean;
  isBusinessClosed?: boolean;
  openingHoursForDay?: {
    is_open: boolean;
    opening_time?: string;
    closing_time?: string;
    is_closed_all_day?: boolean;
  };
  shouldCollapse?: boolean;
  dayKey?: string;
  intervalIndex?: number;
  dayIndex?: number;
  show24Hours?: boolean;
  totalDisplayHours?: number;
  organizedRows?: number;
  isDayExpanded?: boolean;
  rowHeightPx: number;
  hourCellWidthPx: number;
  hideDayColumn?: boolean;
  isEditingMode?: boolean;
  rowIndex?: number;
  isFirstRowInDay?: boolean;
  isLastRowInDay?: boolean;
  gridColumn?: number;
  gridRow?: number;
  isTemplateCreationInProgress?: boolean;
}

const HourCell: React.FC<HourCellProps> = ({
  cellTime,
  isHourMarker = false,
  isWithinBusinessHours = false,
  isBusinessClosed = false,
  openingHoursForDay,
  shouldCollapse = false,
  intervalIndex,
  show24Hours = false,
  totalDisplayHours,
  rowHeightPx,
  isFirstRowInDay = false,
  isLastRowInDay = false,
  gridColumn,
  gridRow,
  isTemplateCreationInProgress = false,
}) => {
  // Opening hours detection logic for visual indicators
  const isOpeningHour = useMemo(() => {
    if (!isWithinBusinessHours || isBusinessClosed || !openingHoursForDay?.opening_time) return false;

    const currentHour = getHours(cellTime);
    const currentMinute = getMinutes(cellTime);

    const [openHour, openMinute] = openingHoursForDay.opening_time.split(':').map(Number);

    return currentHour === openHour && currentMinute === openMinute && isHourMarker;
  }, [isWithinBusinessHours, isBusinessClosed, cellTime, isHourMarker, openingHoursForDay]);

  const isClosingHour = useMemo(() => {
    if (!isWithinBusinessHours || isBusinessClosed || !openingHoursForDay?.closing_time) return false;

    const currentHour = getHours(cellTime);
    const currentMinute = getMinutes(cellTime);

    const [closeHour, closeMinute] = openingHoursForDay.closing_time.split(':').map(Number);

    let lastIntervalHour = closeHour;
    let lastIntervalMinute = closeMinute - 15;

    if (lastIntervalMinute < 0) {
      lastIntervalHour -= 1;
      lastIntervalMinute = 45;
    }

    return currentHour === lastIntervalHour && currentMinute === lastIntervalMinute;
  }, [isWithinBusinessHours, isBusinessClosed, cellTime, openingHoursForDay]);

  // Check if this is the day boundary (24:00/00:00)
  const isDayBoundary = useMemo(() => {
    if (!isHourMarker) return false;

    const currentHour = getHours(cellTime);
    const currentMinute = getMinutes(cellTime);

    return (currentHour === 0 && currentMinute === 0) ||
           (currentHour === 24) ||
           (format(cellTime, 'HH:mm') === '00:00' && currentHour === 0);
  }, [cellTime, isHourMarker]);

  // Build border classes conditionally
  const borderClasses = [
    isFirstRowInDay ? 'border-t' : '',
    isLastRowInDay ? 'border-b' : '',
    isDayBoundary && !show24Hours && totalDisplayHours && totalDisplayHours > 24
      ? 'border-l-2 !border-l-orange-500 dark:!border-l-orange-400'
      : isHourMarker && !isOpeningHour
        ? (isWithinBusinessHours && !isBusinessClosed ? 'border-l !border-l-white dark:!border-l-gray-600' : 'border-l')
        : ''
  ].filter(Boolean).join(' ');

  // Opening hours visual indicators
  const openingHoursBorders = [
    isOpeningHour && !(isDayBoundary && !show24Hours && totalDisplayHours && totalDisplayHours > 24)
      ? 'border-l-2 !border-l-primary'
      : '',
    isClosingHour ? 'border-r-2 !border-r-primary' : ''
  ].filter(Boolean).join(' ');

  // Business hours background styling
  const businessHoursBackground = isWithinBusinessHours && !isBusinessClosed
    ? 'bg-primary/10 dark:bg-primary/15'
    : '';

  // No hover classes - hover is handled by GridDropOverlay
  const hourCellClassName = `${borderClasses} dark:border-gray-600 ${businessHoursBackground} touch-manipulation select-none min-h-[44px] ${openingHoursBorders}`;

  // CSS Grid positioning style
  const gridPositionStyle = {
    minHeight: `${rowHeightPx}px`,
    boxSizing: 'border-box' as const,
    ...(gridColumn && { gridColumn: gridColumn }),
    ...(gridRow && { gridRow: gridRow }),
    // Allow hover styling but let overlay handle clicks
    pointerEvents: 'none' as const,
  };

  return (
    <div
      className={hourCellClassName}
      style={{
        ...gridPositionStyle,
        overflow: shouldCollapse ? 'hidden' : undefined,
        padding: shouldCollapse ? '0' : undefined,
        border: shouldCollapse ? 'none' : undefined,
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none'
      }}
      data-day-key={intervalIndex !== undefined ? `interval-${intervalIndex}` : undefined}
      data-interval={intervalIndex}
    >
      {/* Visual-only cell - interactions handled by GridDropOverlay */}
    </div>
  );
};

/**
 * Custom comparison function for HourCell memoization
 * Simplified since we removed interaction logic
 */
const areHourCellPropsEqual = (prevProps: HourCellProps, nextProps: HourCellProps): boolean => {
  if (
    prevProps.isHourMarker !== nextProps.isHourMarker ||
    prevProps.isWithinBusinessHours !== nextProps.isWithinBusinessHours ||
    prevProps.isBusinessClosed !== nextProps.isBusinessClosed ||
    prevProps.shouldCollapse !== nextProps.shouldCollapse ||
    prevProps.intervalIndex !== nextProps.intervalIndex ||
    prevProps.show24Hours !== nextProps.show24Hours ||
    prevProps.totalDisplayHours !== nextProps.totalDisplayHours ||
    prevProps.rowHeightPx !== nextProps.rowHeightPx ||
    prevProps.isFirstRowInDay !== nextProps.isFirstRowInDay ||
    prevProps.isLastRowInDay !== nextProps.isLastRowInDay ||
    prevProps.gridColumn !== nextProps.gridColumn ||
    prevProps.gridRow !== nextProps.gridRow ||
    prevProps.isTemplateCreationInProgress !== nextProps.isTemplateCreationInProgress
  ) {
    return false;
  }

  // Date comparisons (compare timestamps)
  if (prevProps.cellTime.getTime() !== nextProps.cellTime.getTime()) {
    return false;
  }

  // Object comparisons - reference equality
  if (prevProps.openingHoursForDay !== nextProps.openingHoursForDay) {
    return false;
  }

  return true;
};

export default memo(HourCell, areHourCellPropsEqual);
